<template>
  <el-popover
      v-model="addModal.visible"
      title="Register Accessory"
      placement="left"
      @hide="resetForm">
    <div>
      <el-form
          ref="accessoryForm"
          v-loading="accessoryForm.loading"
          class="register-accessory__form"
          :model="accessoryForm.model"
          :rules="formRules">
        <!-- ACCESSORY TYPES-->
        <el-form-item
            label="Category"
            prop="type">
          <RefreshProducts style="padding-left: 7px" />
          <el-select
              v-model="accessoryForm.model.type"
              data-test-id="accessory-type"
              placeholder="Select">
            <el-option
                v-for="type in accessoryTypes"
                :key="type"
                :label="type"
                :value="type" />
          </el-select>
        </el-form-item>

        <el-form-item
            label="Accessory"
            prop="kind">
          <el-select
              v-model="accessoryForm.model.kind"
              data-test-id="accessory-kind"
              placeholder="Select">
            <el-option
                v-for="device in accessoryKinds[accessoryForm.model.type]"
                :key="device.kind"
                :label="device.description"
                :value="device.kind" />
          </el-select>
        </el-form-item>

        <!-- ACCESSORY ID-->
        <el-form-item
            label="Serial Number"
            prop="id">
          <el-input
              v-model="accessoryForm.model.id"
              data-test-id="accessory-id" />
        </el-form-item>
      </el-form>

      <div
          v-if="accessoryForm.failed.state"
          class="add-new-accessory__alert">
        <el-alert
            v-for="(msg, index) in accessoryForm.failed.messages"
            :key="index"
            type="error"
            :description="msg"
            show-icon
            v-html="msg" />
      </div>


      <div style="text-align: right; margin: 0">
        <el-button
            size="mini"
            type="text"
            @click="resetAndCloseForm">
          cancel
        </el-button>
        <el-button
            type="primary"
            size="mini"
            data-test-id="confirm-btn"
            @click="submitForm">
          confirm
        </el-button>
      </div>
    </div>

    <a slot="reference">Add New</a>
  </el-popover>
</template>

<script>
import axios from "axios";
import RefreshProducts from "@/components/Shared/Products/RefreshProducts";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("shared");

import {
  startCase,
  groupBy,
  sortBy,
  keys,
  cloneDeep,
  get,
  map,
  isEmpty,
} from "lodash";
import { flatMap } from "lodash/collection";

export default {
  name: "AddNewAccessory",
  components: {
    RefreshProducts,
  },
  props: {
    customerProfileId: {
      type: Number,
      required: true,
    },
    callback: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      accessoryForm: {
        model: {
          type: null,
          kind: null,
          id: "",
        },
        loading: false,
        failed: {
          state: false,
          message: "",
        },
      },
      formRules: {
        id: [
          {
            required: true,
            pattern: /^[a-zA-Z0-9]{16,20}$/,
            message:
              "Serial number must be from 16 until 20 alphanumerical characters",
            trigger: "blur",
          },
        ],
        type: [
          {
            required: true,
            message: "Please select one category of accessory",
            trigger: "change",
          },
        ],
        kind: [
          {
            required: true,
            message: "Please select one accessory",
            trigger: "change",
          },
        ],
      },
      addModal: { visible: false },
      accessoryTypes: [],
      accessoryKinds: {},
    };
  },
  computed: {
    accessoriesGetter() {
      return this.$store.getters["shared/accessories"];
    },
  },
  watch: {
    accessoriesGetter: {
      handler(newValue, oldValue) {
        this.setupAccessories();
      },
    },
  },
  mounted() {
    this.setupAccessories();
  },
  methods: {
    setupAccessories(data) {
      let accessories = cloneDeep(this.accessoriesGetter);
      accessories = accessories.filter((acc) => acc.enabled);
      let filtered = accessories.filter((acc) => {
        acc.device = acc.device === "hub" ? "alarm" : acc.device; // display Hubs as Alarms
        acc.device = startCase(acc.device);
        return acc.enabled;
      });
      let groupedAccessories = groupBy(filtered, (acc) => acc.device);
      let groups = keys(groupedAccessories);

      this.accessoryTypes = groups;
      groups.forEach(
        (group) =>
          (this.accessoryKinds[group] = sortBy(
            groupedAccessories[group],
            (device) => device.description
          ))
      );
    },
    submitForm() {
      this.$refs.accessoryForm.validate((valid) => {
        if (valid) this.createAccessory();
        else return false;
      });
    },
    resetAndCloseForm() {
      this.resetForm();
      this.addModal.visible = false;
    },
    resetForm() {
      this.accessoryForm.failed.state = false;
      this.accessoryForm.failed.messages = [];
      this.$refs.accessoryForm.resetFields();
    },
    createAccessory() {
      this.startRequest();
      axios
        .post(`v2/devices/${this.accessoryForm.model.id}/create_accessory`, {
          type: "accessory",
          accessory_profile: {
            customer_profile_id: this.customerProfileId,
            kind: this.accessoryForm.model.kind,
            device_id: this.accessoryForm.model.id,
            state: "at_home",
          },
        })
        .then((response) => {
          this.$message({
            showClose: true,
            message: "Accessory registered",
            type: "success",
          });
          this.callback(response.data);
          this.resetAndCloseForm();
        })
        .catch((error) => {
          this.accessoryForm.failed.state = true;
          this.processResponseErrors(error);
        })
        .finally(() => this.endRequest());
    },
    endRequest() {
      this.accessoryForm.loading = false;
    },
    startRequest() {
      this.accessoryForm.failed.state = false;
      this.accessoryForm.failed.messages = [];
      this.accessoryForm.loading = true;
    },
    processResponseErrors(error) {
      let errorMessages = [];
      if (error.status === 400) {
        const errors = get(error, "data.body.message");
        if (errors) errorMessages.push(errors);
      } else if (error.status === 422) {
        errorMessages = flatMap(
          get(error, "data.body.errors", []),
          (messages, _) => map(messages, (message) => message)
        );
      }
      if (isEmpty(errorMessages))
        errorMessages = [
          "<p>We’re sorry, but something went wrong. Please try again later. \n" +
            "If the problem persists, contact support at " +
            '<a href="https://ring.enterprise.slack.com/archives/C022VTK396E" target="_blank">#nd-support</a></p>',
        ];
      this.accessoryForm.failed.messages = errorMessages;
    },
  },
};
</script>

<style lang="scss" scoped>
.add-new-accessory__alert {
  p {
    text-align: left;
  }
  word-break: break-word;
  .el-alert--error {
    margin-top: 1px;
  }
  margin: 10px 7px;
  & div[role="alert"].el-alert__title {
    font-size: 10px;
  }
}
.register-accessory__form {
  margin: 13px;
}
</style>
